import { useEffect, useCallback } from 'react';
import { SITE_TITLE, HEADER_UI } from '../constants';
import ThemeToggle from './ThemeToggle';

interface HeaderProps {
  onMenuClick: () => void;
  onSearchOpen: () => void;
}

export default function Header({ onMenuClick, onSearchOpen }: HeaderProps) {
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        onSearchOpen();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [onSearchOpen]);

  const handleNavClick = useCallback((e: React.MouseEvent, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth' });
  }, []);

  return (
    <header className="sticky top-0 z-30 flex h-16 w-full items-center justify-between border-b border-slate-900 bg-slate-950/80 px-4 backdrop-blur-md lg:px-8">
      <div className="flex items-center gap-4">
        {/* Mobile menu toggle */}
        <button
          type="button"
          onClick={onMenuClick}
          className="lg:hidden p-2 text-slate-400 hover:text-white transition-colors"
          aria-label="Open navigation menu"
        >
          <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
          </svg>
        </button>

        {/* Breadcrumb */}
        <nav className="hidden lg:flex items-center gap-2 text-sm text-slate-400" aria-label="Breadcrumb">
          <span>{HEADER_UI.breadcrumbPrefix}</span>
          <span className="text-slate-700" aria-hidden="true">/</span>
          <a
            href="#intro"
            onClick={(e) => handleNavClick(e, 'intro')}
            className="text-white font-medium hover:text-emerald-400 transition-colors"
            aria-current="page"
          >
            {SITE_TITLE}
          </a>
        </nav>
      </div>

      <div className="flex flex-1 items-center justify-end gap-4 sm:gap-6 max-w-xl">
        {/* Search trigger */}
        <button
          type="button"
          onClick={onSearchOpen}
          className="flex flex-1 items-center gap-3 rounded-xl border border-slate-800 bg-slate-900/40 py-2 pl-4 pr-2 text-sm text-slate-400 hover:border-emerald-500/50 transition-all hover:bg-slate-900 group relative"
          role="search"
          aria-label="Search documentation (Ctrl+K)"
        >
          <svg className="h-4 w-4 text-slate-500 group-hover:text-emerald-500 transition-colors" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
          </svg>
          <span className="flex-1 text-left font-medium group-hover:text-slate-300 transition-colors truncate">
            {HEADER_UI.searchPlaceholder}
          </span>
          <kbd className="hidden sm:inline-flex items-center gap-1.5 rounded-lg border border-emerald-500/30 bg-emerald-500/10 px-2 py-1 font-sans text-[10px] font-black text-emerald-400 uppercase tracking-widest shadow-[0_0_15px_-3px_rgba(16,185,129,0.3)] group-hover:border-emerald-500/60 group-hover:bg-emerald-500/20 transition-all">
            <span aria-hidden="true">⌘</span>
            <span>K</span>
          </kbd>
        </button>

        {/* Theme toggle — always visible */}
        <ThemeToggle />

        {/* Header links */}
        <div className="hidden sm:flex items-center gap-6">
          <a
            href="#support-demo"
            onClick={(e) => handleNavClick(e, 'support-demo')}
            className="text-sm font-medium text-slate-400 hover:text-white transition-colors"
          >
            {HEADER_UI.supportLabel}
          </a>
          <a
            href="#intro"
            onClick={(e) => handleNavClick(e, 'intro')}
            className="rounded-full bg-emerald-500 px-5 py-2 text-sm font-bold text-white hover:bg-emerald-400 transition-all shadow-lg shadow-emerald-500/20 active:scale-95"
          >
            {HEADER_UI.ctaLabel}
          </a>
        </div>
      </div>
    </header>
  );
}
